import { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { Gym, GymClaimRequest, GymChallenge, GymChallengeParticipant } from '@/types/database';
import { MapPin, Award, Clock, CheckCircle, XCircle, Trophy, Calendar, Users, Plus, Edit3 } from 'lucide-react-native';
import GymEditForm from '@/components/GymEditForm';
import Constants from 'expo-constants';
import { FLAGS } from '@/lib/featureFlags';

const DEV_ADMIN_MODE = Constants.expoConfig?.extra?.EXPO_PUBLIC_DEV_ADMIN_MODE === 'true';

export default function GymProfileScreen() {
  const { gymId } = useLocalSearchParams<{ gymId: string }>();
  const router = useRouter();
  const { user } = useAuth();
  const { theme } = useTheme();

  const [gym, setGym] = useState<Gym | null>(null);
  const [claimRequest, setClaimRequest] = useState<GymClaimRequest | null>(null);
  const [challenges, setChallenges] = useState<GymChallenge[]>([]);
  const [participants, setParticipants] = useState<GymChallengeParticipant[]>([]);
  const [participantCounts, setParticipantCounts] = useState<Record<string, number>>({});
  const [isOwner, setIsOwner] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [joiningChallengeId, setJoiningChallengeId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [showEditForm, setShowEditForm] = useState(false);

  useEffect(() => {
    loadGymData();
  }, [gymId]);

  const loadGymData = async () => {
    if (!gymId) return;

    setLoading(true);
    setError('');

    try {
      const { data: gymData, error: gymError } = await supabase
        .from('gyms')
        .select('*')
        .eq('id', gymId)
        .single();

      if (gymError) throw gymError;
      setGym(gymData);

      const { data: requestData } = await supabase
        .from('gym_claim_requests')
        .select('*')
        .eq('gym_id', gymId)
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      setClaimRequest(requestData);

      const { data: membershipData } = await supabase
        .from('gym_memberships')
        .select('role')
        .eq('gym_id', gymId)
        .eq('user_id', user!.id)
        .eq('status', 'active')
        .maybeSingle();

      if (membershipData) {
        setIsMember(true);
        setIsOwner(membershipData.role === 'owner');

        const { data: challengesData } = await supabase
          .from('gym_challenges')
          .select('*')
          .eq('gym_id', gymId)
          .eq('is_active', true)
          .order('start_date', { ascending: false });

        setChallenges(challengesData || []);

        const { data: participantsData } = await supabase
          .from('gym_challenge_participants')
          .select('*')
          .eq('user_id', user!.id);

        setParticipants(participantsData || []);

        if (challengesData && challengesData.length > 0) {
          const challengeIds = challengesData.map((c) => c.id);
          const { data: allParticipants } = await supabase
            .from('gym_challenge_participants')
            .select('challenge_id')
            .in('challenge_id', challengeIds);

          if (allParticipants) {
            const counts: Record<string, number> = {};
            allParticipants.forEach((p) => {
              counts[p.challenge_id] = (counts[p.challenge_id] || 0) + 1;
            });
            setParticipantCounts(counts);
          }
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClaim = async () => {
    if (!gym || !claimRequest) return;

    Alert.alert(
      'Approve Claim',
      'Are you sure you want to approve this claim? This will make the user the gym owner.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Approve',
          style: 'destructive',
          onPress: async () => {
            setApproving(true);
            setError('');

            try {
              const { error: requestError } = await supabase
                .from('gym_claim_requests')
                .update({
                  status: 'approved',
                  reviewed_at: new Date().toISOString(),
                  reviewed_by: user!.id,
                })
                .eq('id', claimRequest.id);

              if (requestError) throw requestError;

              const { error: gymError } = await supabase
                .from('gyms')
                .update({
                  claim_status: 'claimed',
                  claimed_by_user_id: claimRequest.user_id,
                  claimed_at: new Date().toISOString(),
                })
                .eq('id', gym.id);

              if (gymError) throw gymError;

              const { error: membershipError } = await supabase
                .from('gym_memberships')
                .upsert({
                  gym_id: gym.id,
                  user_id: claimRequest.user_id,
                  role: 'owner',
                  status: 'active',
                }, {
                  onConflict: 'gym_id,user_id',
                });

              if (membershipError) throw membershipError;

              Alert.alert('Approved', 'Claim Approved');
              loadGymData();
            } catch (err: any) {
              setError(err.message);
            } finally {
              setApproving(false);
            }
          },
        },
      ]
    );
  };

  const handleJoinChallenge = async (challengeId: string) => {
    setJoiningChallengeId(challengeId);
    setError('');

    try {
      const { error: joinError } = await supabase
        .from('gym_challenge_participants')
        .insert({
          challenge_id: challengeId,
          user_id: user!.id,
        });

      if (joinError) throw joinError;

      Alert.alert('Joined', 'Challenge Entered');
      loadGymData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setJoiningChallengeId(null);
    }
  };

  const handleLeaveChallenge = async (challengeId: string) => {
    setJoiningChallengeId(challengeId);
    setError('');

    try {
      const { error: leaveError } = await supabase
        .from('gym_challenge_participants')
        .delete()
        .eq('challenge_id', challengeId)
        .eq('user_id', user!.id);

      if (leaveError) throw leaveError;

      Alert.alert('Exited', 'Challenge Exited');
      loadGymData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setJoiningChallengeId(null);
    }
  };

  const isJoined = (challengeId: string) => {
    return participants.some((p) => p.challenge_id === challengeId);
  };

  const getClaimStatusColor = (status?: string) => {
    switch (status) {
      case 'pending':
        return theme.colors.warning;
      case 'approved':
        return theme.colors.success;
      case 'rejected':
        return theme.colors.error;
      default:
        return theme.colors.textSecondary;
    }
  };

  const getClaimStatusIcon = (status?: string) => {
    switch (status) {
      case 'pending':
        return <Clock size={16} color={theme.colors.warning} />;
      case 'approved':
        return <CheckCircle size={16} color={theme.colors.success} />;
      case 'rejected':
        return <XCircle size={16} color={theme.colors.error} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.accent} />
      </View>
    );
  }

  if (!gym) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Text style={[styles.errorText, { color: theme.colors.text }]}>Gym not found</Text>
      </View>
    );
  }

  const canClaim = !gym.claim_status || gym.claim_status === 'unclaimed';
  const hasPendingClaim = claimRequest?.status === 'pending';

  if (showEditForm && gym) {
    return (
      <GymEditForm
        gym={{ id: gym.id, name: gym.name, address: gym.address ?? undefined, city: gym.city ?? undefined, state: gym.state ?? undefined }}
        onSubmitted={() => { setShowEditForm(false); loadGymData(); }}
        onCancel={() => setShowEditForm(false)}
      />
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <View style={{ width: 24 }} />
        <Text style={[styles.headerTitle, { color: theme.colors.text }]}>GYM PROFILE</Text>
        <View style={{ width: 24 }} />
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={styles.content}>
          <View style={styles.gymHeader}>
            <MapPin size={40} color={theme.colors.accent} />
            <Text style={[styles.gymName, { color: theme.colors.text }]}>{gym.name}</Text>
            {gym.address && (
              <Text style={[styles.gymAddress, { color: theme.colors.textSecondary }]}>
                {gym.address}
              </Text>
            )}
            <TouchableOpacity
              style={styles.suggestEditButton}
              onPress={() => setShowEditForm(true)}
            >
              <Edit3 size={14} color={theme.colors.textTertiary} />
              <Text style={[styles.suggestEditText, { color: theme.colors.textTertiary }]}>
                Suggest an edit
              </Text>
            </TouchableOpacity>
          </View>

          {error ? <Text style={[styles.errorMessage, { color: theme.colors.error, backgroundColor: theme.colors.surface, borderColor: theme.colors.error }]}>{error}</Text> : null}

          {FLAGS.V1_GYM_CLAIMING_ENABLED && (
          <View style={[styles.statusCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
            <View style={styles.statusRow}>
              <Text style={[styles.label, { color: theme.colors.textSecondary }]}>Claim Status</Text>
              <View style={styles.statusBadge}>
                <Text
                  style={[
                    styles.statusText,
                    { color: getClaimStatusColor(gym.claim_status) },
                  ]}>
                  {gym.claim_status || 'unclaimed'}
                </Text>
              </View>
            </View>

            {gym.claim_status === 'claimed' && gym.claimed_at && (
              <Text style={[styles.timestamp, { color: theme.colors.textTertiary }]}>
                Claimed on {new Date(gym.claimed_at).toLocaleDateString()}
              </Text>
            )}
          </View>
          )}

          {FLAGS.V1_GYM_CLAIMING_ENABLED && claimRequest && (
            <View style={[styles.claimCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
              <View style={styles.claimHeader}>
                {getClaimStatusIcon(claimRequest.status)}
                <Text style={[styles.claimTitle, { color: theme.colors.text }]}>
                  Your Claim Request
                </Text>
              </View>
              <View style={styles.claimDetail}>
                <Text style={[styles.label, { color: theme.colors.textSecondary }]}>Status</Text>
                <Text style={[styles.value, { color: getClaimStatusColor(claimRequest.status) }]}>
                  {claimRequest.status}
                </Text>
              </View>
              <View style={styles.claimDetail}>
                <Text style={[styles.label, { color: theme.colors.textSecondary }]}>Submitted</Text>
                <Text style={[styles.value, { color: theme.colors.text }]}>
                  {new Date(claimRequest.created_at).toLocaleDateString()}
                </Text>
              </View>
              {claimRequest.proof_notes && (
                <View style={styles.claimDetail}>
                  <Text style={[styles.label, { color: theme.colors.textSecondary }]}>Notes</Text>
                  <Text style={[styles.value, { color: theme.colors.text }]}>
                    {claimRequest.proof_notes}
                  </Text>
                </View>
              )}
            </View>
          )}

          {FLAGS.V1_GYM_CLAIMING_ENABLED && !isOwner && canClaim && !hasPendingClaim && (
            <TouchableOpacity
              style={[styles.claimButton, { backgroundColor: theme.colors.accent }]}
              onPress={() => router.push({ pathname: '/(gym)/claim', params: { gymId: gym.id } })}>
              <Award size={20} color={theme.colors.text} />
              <Text style={[styles.claimButtonText, { color: theme.colors.text }]}>
                Claim This Gym
              </Text>
            </TouchableOpacity>
          )}

          {FLAGS.V1_GYM_CLAIMING_ENABLED && DEV_ADMIN_MODE && hasPendingClaim && (
            <TouchableOpacity
              style={[styles.approveButton, { backgroundColor: theme.colors.success }]}
              onPress={handleApproveClaim}
              disabled={approving}>
              {approving ? (
                <ActivityIndicator color={theme.colors.text} />
              ) : (
                <>
                  <CheckCircle size={20} color={theme.colors.text} />
                  <Text style={[styles.approveButtonText, { color: theme.colors.text }]}>Approve Claim (Dev Mode)</Text>
                </>
              )}
            </TouchableOpacity>
          )}

          {isOwner && (
            <TouchableOpacity
              style={[
                styles.joinCodeButton,
                { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
              ]}
              onPress={() => router.push('/gym-join-code')}>
              <Users size={20} color={theme.colors.accent} />
              <Text style={[styles.joinCodeText, { color: theme.colors.text }]}>
                Show Join Code
              </Text>
            </TouchableOpacity>
          )}

          {FLAGS.V1_CHALLENGES_ENABLED && isMember && (
            <>
              <View style={styles.sectionHeader}>
                <View style={styles.sectionTitleRow}>
                  <Trophy size={24} color={theme.colors.accent} />
                  <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
                    Challenges
                  </Text>
                </View>
                {isOwner && (
                  <TouchableOpacity
                    style={[styles.createButton, { backgroundColor: theme.colors.accent }]}
                    onPress={() => router.push(`/(gym)/${gymId}/create-challenge`)}>
                    <Plus size={20} color={theme.colors.text} />
                  </TouchableOpacity>
                )}
              </View>

              {challenges.length === 0 ? (
                <View style={[styles.emptyCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
                  <Trophy size={32} color={theme.colors.textTertiary} />
                  <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
                    {isOwner ? 'Create your first challenge!' : 'No active challenges yet'}
                  </Text>
                </View>
              ) : (
                challenges.map((challenge) => {
                  const joined = isJoined(challenge.id);
                  const isLoading = joiningChallengeId === challenge.id;

                  return (
                    <View
                      key={challenge.id}
                      style={[
                        styles.challengeCard,
                        {
                          backgroundColor: theme.colors.surface,
                          borderColor: joined ? theme.colors.accent : theme.colors.border,
                        },
                      ]}>
                      <Text style={[styles.challengeTitle, { color: theme.colors.text }]}>
                        {challenge.title}
                      </Text>
                      {challenge.description && (
                        <Text style={[styles.challengeDescription, { color: theme.colors.textSecondary }]}>
                          {challenge.description}
                        </Text>
                      )}
                      <View style={styles.challengeMeta}>
                        <View style={styles.metaRow}>
                          <Calendar size={14} color={theme.colors.textTertiary} />
                          <Text style={[styles.metaText, { color: theme.colors.textSecondary }]}>
                            {new Date(challenge.start_date).toLocaleDateString()} -{' '}
                            {new Date(challenge.end_date).toLocaleDateString()}
                          </Text>
                        </View>
                        {challenge.reward && (
                          <View style={styles.metaRow}>
                            <Award size={14} color={theme.colors.textTertiary} />
                            <Text style={[styles.metaText, { color: theme.colors.textSecondary }]}>
                              {challenge.reward}
                            </Text>
                          </View>
                        )}
                        <View style={styles.metaRow}>
                          <Users size={14} color={theme.colors.textTertiary} />
                          <Text style={[styles.metaText, { color: theme.colors.textSecondary }]}>
                            {participantCounts[challenge.id] || 0} participant
                            {participantCounts[challenge.id] !== 1 ? 's' : ''}
                          </Text>
                        </View>
                      </View>
                      {challenge.winner_user_id && (
                        <View style={[styles.winnerBadge, { backgroundColor: theme.colors.accent + '20', borderColor: theme.colors.accent }]}>
                          <Trophy size={14} color={theme.colors.accent} />
                          <Text style={[styles.winnerText, { color: theme.colors.accent }]}>Winner Selected</Text>
                        </View>
                      )}
                      <TouchableOpacity
                        style={[styles.viewParticipantsButton, { borderColor: theme.colors.border }]}
                        onPress={() => router.push(`/(gym)/${gymId}/challenge/${challenge.id}/participants`)}>
                        <Users size={16} color={theme.colors.textSecondary} />
                        <Text style={[styles.viewParticipantsText, { color: theme.colors.textSecondary }]}>
                          View Participants
                        </Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[
                          styles.joinButton,
                          {
                            backgroundColor: joined ? theme.colors.surface : theme.colors.accent,
                            borderColor: theme.colors.accent,
                          },
                          joined && styles.joinedButton,
                        ]}
                        onPress={() =>
                          joined ? handleLeaveChallenge(challenge.id) : handleJoinChallenge(challenge.id)
                        }
                        disabled={isLoading}>
                        {isLoading ? (
                          <ActivityIndicator size="small" color={joined ? theme.colors.accent : theme.colors.text} />
                        ) : (
                          <>
                            {joined ? (
                              <>
                                <CheckCircle size={16} color={theme.colors.accent} />
                                <Text style={[styles.joinButtonText, { color: theme.colors.accent }]}>
                                  Joined
                                </Text>
                              </>
                            ) : (
                              <>
                                <Users size={16} color={theme.colors.text} />
                                <Text style={[styles.joinButtonText, { color: theme.colors.text }]}>
                                  Join Challenge
                                </Text>
                              </>
                            )}
                          </>
                        )}
                      </TouchableOpacity>
                    </View>
                  );
                })
              )}
            </>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 60,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '900',
    letterSpacing: 1.5,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
  },
  content: {
    gap: 20,
  },
  gymHeader: {
    alignItems: 'center',
    gap: 12,
    paddingVertical: 20,
  },
  gymName: {
    fontSize: 24,
    fontWeight: '900',
    textAlign: 'center',
  },
  gymAddress: {
    fontSize: 14,
    textAlign: 'center',
  },
  suggestEditButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  suggestEditText: {
    fontSize: 13,
    fontWeight: '500',
  },
  statusCard: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    gap: 8,
  },
  statusRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  timestamp: {
    fontSize: 12,
  },
  claimCard: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    gap: 12,
  },
  claimHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  claimTitle: {
    fontSize: 16,
    fontWeight: '700',
  },
  claimDetail: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  value: {
    fontSize: 14,
    fontWeight: '600',
  },
  claimButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    borderRadius: 2,
    marginTop: 8,
  },
  claimButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
  approveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    borderRadius: 2,
    marginTop: 8,
  },
  approveButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
  joinCodeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 2,
    borderWidth: 1,
    marginTop: 8,
  },
  joinCodeText: {
    fontSize: 15,
    fontWeight: '700',
  },
  errorText: {
    fontSize: 16,
    textAlign: 'center',
    padding: 20,
  },
  errorMessage: {
    fontSize: 14,
    textAlign: 'center',
    padding: 12,
    borderRadius: 2,
    borderWidth: 1,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 12,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '900',
    letterSpacing: 0.5,
  },
  createButton: {
    padding: 8,
    borderRadius: 2,
  },
  emptyCard: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 32,
    alignItems: 'center',
    gap: 12,
  },
  emptyText: {
    fontSize: 14,
    textAlign: 'center',
  },
  challengeCard: {
    borderWidth: 2,
    borderRadius: 2,
    padding: 16,
    gap: 12,
  },
  challengeTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  challengeDescription: {
    fontSize: 14,
    lineHeight: 20,
  },
  challengeMeta: {
    gap: 8,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaText: {
    fontSize: 12,
  },
  joinButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    borderRadius: 2,
    marginTop: 4,
  },
  joinedButton: {
    borderWidth: 2,
  },
  joinButtonText: {
    fontSize: 14,
    fontWeight: '700',
  },
  winnerBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 2,
    borderWidth: 1,
  },
  winnerText: {
    fontSize: 12,
    fontWeight: '700',
  },
  viewParticipantsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    borderRadius: 2,
    borderWidth: 1,
  },
  viewParticipantsText: {
    fontSize: 13,
    fontWeight: '600',
  },
});
